import { useEffect } from 'react';
import { Stack } from '@mui/material';

import SideBar from '@/components/ui/SideBar';
import AppBar from '@/components/AppBar';
import GoogleCalendarBanner from '@/components/GoogleCalendarBanner';
import VersionBanner from '@/components/VersionBanner';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  SideBarSANavigation,
  SideBarVVNavigation,
  SideBarVMNavigation,
  SideBarVPNavigation,
  SideBarBONavigation,
} from '@/constants/sidebar';
import ScreenWrapper from '@/components/ui/ScreenWrapper';
import usePermissions from '@/hooks/usePermissions';
import { SideBarItemData } from '@/components/ui/SideBarItem';
import { getPath } from '@/utils';
import { Routes } from '@/constants/routes';
import { unteradminSidebar } from './unteradminSidebar';
import useNotificationStore from '@/store/notifications';
import FabAktionenProvider, { FabSchutzflaeche } from '@/components/feedback/FabAktionen';
import ChangelogModal from '@/components/modals/ChangelogModal';
import AdminAlarmModal from '@/components/modals/AdminAlarmModal';
import KundeWartetPopup from '@/components/modals/KundeWartetPopup';
import { subtleScrollbar } from '@/theme/scrollbar';
import { InhaltsFlaeche, NavyRahmen } from '@/components/ui/Rahmen';

// Inaktivitaets-Auto-Logout entfernt (Entscheid 2026-07-11): Logout nur noch
// manuell oder ueber Token-Ablauf (Sanctum 24h). Die frueheren 18-min-Warnung
// und 20-min-Zwangs-Logout (useInactivityTimer + InactivityWarningModal) sind
// abgeschaltet — siehe docs/daily-logs/2026-07-11.md.
const HomeView = () => {
  const { select, isTelefonie, isWizardOnlyRole, isUnteradmin, hatBereich, darfPfad } =
    usePermissions();
  const { startPolling, stopPolling } = useNotificationStore();

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const navigate = useNavigate();
  const location = useLocation();

  const handleSideBarItemClick = (item: SideBarItemData) => {
    navigate(`/${getPath(Routes.Home, item.path)}`);
  };

  const alleSideBarItems =
    select([
      [['users', 'customers', 'teams'], SideBarSANavigation],
      [['teams', 'contacts', 'meetings', 'pipeline', 'reports'], SideBarVVNavigation],
      [['teams', 'contacts', 'meetings', 'meeting-pools'], SideBarVMNavigation],
      [['contacts', 'meetings', 'documents'], SideBarBONavigation],
      [['customers', 'contacts'], SideBarVPNavigation],
    ]) || [];

  // Unteradmin (Entscheid Alex 16.10.2026 20:42): SA-Liste minus
  // ausgeschaltete Bereiche. Fuer alle anderen Rollen unveraendert.
  // Ohne Eintraege nur fuer die echte Rolle `sa` (API-Schluessel, Decision #252 H).
  const sideBarItems = isUnteradmin()
    ? unteradminSidebar(alleSideBarItems, hatBereich)
    : alleSideBarItems;

  // Onboarding-Rolle `telefonie` (Vorgabe Alex 2026-07-19): Zugriff nur auf
  // die Telefonie-Seite. Ein Direktaufruf jeder anderen /home-Unterroute wird
  // hier zentral auf den Wizard zurueckgeworfen — eine Stelle statt eines
  // Guards pro Route, damit eine spaeter ergaenzte Route die Sperre nicht
  // stillschweigend umgeht.
  //
  // Das ist NUR die UI-Seite. Die verbindliche Sperre ist serverseitig
  // (Permission-Set + Visibility `none` -> 403 auf allen Fach-Endpoints);
  // siehe docs/tasks/rolle-telefonie-onboarding.md.
  const callcenterPath = `/${getPath(Routes.Home, Routes.Callcenter)}`;

  if (isTelefonie() && location.pathname !== callcenterPath) {
    return <Navigate replace to={callcenterPath} />;
  }

  // Unteradmin: ausgeschalteter Bereich auch per direkter Adresse zu (E3) —
  // Weiterleitung auf den ersten erlaubten Eintrag (E7), ohne jeden Bereich
  // auf die Benachrichtigungen. Verbindlich sperrt das Backend.
  if (isUnteradmin() && !darfPfad(location.pathname)) {
    const ziel = sideBarItems[0]?.path ?? Routes.Notifications;
    return <Navigate replace to={`/${getPath(Routes.Home, ziel)}`} />;
  }

  return (
    <ScreenWrapper flexDirection="column">
      {/* EIN FAB-System (Punkt 6, 10.09.2026): der Provider umschliesst den
          Outlet, damit die Detailseiten darunter ihren Menueeintrag „Ticket
          erstellen" anhaengen und ihren eigenen FAB melden koennen. Wo keine
          das tut, rendert er selbst den Knopf. */}
      <FabAktionenProvider>
      {/* Navy-Rahmen, Kopfband und Rundecke sind gemeinsame Bausteine mit der
          Beratungsstrecke (`@/components/ui/Rahmen`, Nacharbeit 27.10.2026). */}
      <NavyRahmen>
        {!isWizardOnlyRole() && (
          <SideBar
            items={sideBarItems}
            onSideBarItemClick={handleSideBarItemClick}
            collapsible={true}
          />
        )}
        <Stack flex={1} minHeight={0} direction="column" overflow="hidden">
          {!isWizardOnlyRole() && <AppBar />}
          {/* Kalender-Banner ganz oben, auf JEDER /home-Seite — der Wizard
              (/home/telefonie) ist eine Kind-Route dieses Layouts und wird
              damit mit abgedeckt, obwohl er keine AppBar rendert.
              Die Rollen-Regel (CC nie, SA nur Reconnect, VP+Telefonie beide
              Faelle) steht vollstaendig in der Komponente — bewusst NICHT
              hier zusaetzlich gaten, sonst gibt es zwei Stellen, die sich
              widersprechen koennen. */}
          {/* Rundecke zwischen Navy-Rahmen und Inhalt wieder wie im Original
              2272451fa (16px oben links, Korrekturrunde 1, Alex 26.10.2026). */}
          <InhaltsFlaeche>
            {/* BANNER STEHEN INNERHALB dieses Containers, nicht darueber.
                (Korrektur 23.07.2026, Screenshot-Befund Alex.)

                Die Deko-Ecke oben links ist eine Ausstanzung: der
                radial-gradient malt `primary.darker` um einen transparenten
                Radius herum und setzt damit voraus, dass direkt darueber auch
                wirklich `primary.darker` liegt. Standen die Banner davor,
                schob sich deren Flaeche (`secondary.sky` bzw. die Fehlerfarbe
                des Kalender-Banners) zwischen Ecke und dunklen Hintergrund —
                die Ecke stanzte die falsche Farbe aus und die Rundung sass
                sichtbar daneben.

                Innerhalb des Containers gilt die Rundung fuer den gesamten
                Inhalt inklusive Banner, und die Ausstanzung trifft wieder den
                dunklen Bereich der AppBar-Zone. Die Mount-Stelle bleibt damit
                fachlich dieselbe (ueber JEDER /home-Seite, auch dem Wizard als
                Kind-Route ohne eigene AppBar) — nur eine Ebene tiefer. */}
            <GoogleCalendarBanner />
            {/* Version-Skew-Hinweis, gleiche Stelle und aus demselben Grund:
                der Wizard (/home/telefonie) rendert keine eigene AppBar. Genau
                diese Kohorte hat der Skew am 22.07. getroffen (6 von 6 stale
                Tabs). Rendert sich selbst weg, solange Bundle und Server
                zusammenpassen. */}
            <VersionBanner />
            {/* Der Scroll-Container der Shell — auf JEDER Seite derselbe.
                Weil er ueber die volle Fensterhoehe laeuft, sah sein
                Standardbalken aus wie der des Browsers (Befund im
                Kalender-Nachtrag, feedback-fixes-2026-07-22.md).
                `subtleScrollbar` aendert AUSSCHLIESSLICH sein Aussehen:
                overflow, Hoehe und Flex-Verhalten bleiben unveraendert, damit
                keine einzelne Seite still ihr Scroll-Verhalten wechselt. */}
            <Stack flex={1} minHeight={0} overflow="auto" sx={subtleScrollbar}>
              <Outlet />
              {/* Platz unter dem Seitenende, solange der globale „+"-Knopf
                  steht (Feedback 13.10.2026: er verdeckte auf /home/meetings
                  den Weiter-Pfeil der Paginierung). Gilt fuer jede Seite;
                  Seiten mit eigenem FAB bekommen keine Flaeche. */}
              <FabSchutzflaeche />
            </Stack>
          </InhaltsFlaeche>
        </Stack>
      </NavyRahmen>
      </FabAktionenProvider>
      {/* "Das ist neu" nach dem Login (Fix 6, feedback-fixes-2026-07-23.md).
          Nicht fuer cc und telefonie (Alex 01.10.2026, N4): das BE laesst die
          Route nur fuer sa|vp zu, das Gate hier spart den 403-Roundtrip. */}
      {!isWizardOnlyRole() && <ChangelogModal />}
      {/* Kritische System-Meldungen an Admin/SA (Decision #207): Popup bis Quittierung. */}
      {!isTelefonie() && <AdminAlarmModal />}
      {/* Popup „Mandant wartet" (Paket 1, belieferung-doppelbelegung.md §5N.2):
          nur eigene Termine, Schalter serverseitig. Welche Rollen fragen
          (positiv: VP, SA), entscheidet die Komponente selbst. */}
      <KundeWartetPopup />
    </ScreenWrapper>
  );
};

export default HomeView;
